/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { URL_API } from "../config/default";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { myHeaders } from "../config/axios";
import { CustomToast } from "../utils/custom-toast";
import Error from "./Error/Index";
import AccommodationCard from "../components/UI/Accommodation-card";
import { Heart } from "lucide-react";
import { AccommodationMap } from "../components/UI/accommodation-map";
import { useEffect, useState, useCallback, useRef } from "react";
import { useAppContext } from "../contexts/AppProvider";
import { useSearchParams } from "react-router-dom";
import { Skeleton } from "../components/UI/Skeleton";
import useMedia from "../hooks/useMedia";
import { chunkArray } from "../utils/chunkArray";
import { SliderChunk } from "../components/UI/Home/slider-section";

const Alojamientos = () => {
  const isMobile = useMedia("(max-width: 768px)", false);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { me }: any = useAppContext();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const setFavorito = (property: any, favoritos: any) => {
    let check = favoritos.data.filter((f: any) => f.property_id == property.id);
    if (check.length > 0) {
      const requestOptions = {
        method: "DELETE",
        headers: {
          Authorization: myHeaders,
        },
      };

      fetch(`${URL_API}/favorites/${check[0].id}`, requestOptions)
        .then(async (response) => {
          await response.json();
          return CustomToast({
            status: 200,
            msg: "",
            title: "Alojamiento eliminado de favoritos",
          });
        })
        .catch((error) => console.error(error));

      queryClient.invalidateQueries({
        queryKey: ["favoritos"],
      });
      return ""
    }

    let formData = new FormData();
    formData.append("property_id", String(property.id));

    const requestOptions = {
      method: "POST",
      body: formData,
      headers: {
        Authorization: myHeaders,
      },
    };

    fetch(`${URL_API}/favorites`, requestOptions)
      .then(async (response) => {
        if (response.ok) {
          await response.json();
          return CustomToast({
            status: 200,
            msg: "",
            title: "Alojamiento agregado a favoritos",
          });
        } else {
          return CustomToast({
            status: 400,
            msg: "",
            title: "Debes inicisar sesion para generar una lista de favoritos",
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });

    queryClient.invalidateQueries({
      queryKey: ["favoritos"],
    });
  };

  const [filter, setFilter] = useState<any>({
    location: searchParams.get("location"),
    travellers: parseInt(searchParams.get("travellers") || "0"),
    startDate: searchParams.get("startDate"),
    endDate: searchParams.get("endDate"),
    lat: searchParams.get("lat"),
    lon: searchParams.get("lon"),
    space: searchParams.get("space") || "",
    description: searchParams.get("description") || "",
    activities: searchParams.get("activities") || "",
    type: searchParams.get("type") || "",
    services: searchParams.get("services") || "",
    bedrooms: parseInt(searchParams.get("bedrooms") || "0"),
    beds: parseInt(searchParams.get("beds") || "0"),
    bathrooms: parseInt(searchParams.get("bathrooms") || "0"),
    priceMin: parseInt(searchParams.get("priceMin") || "0"),
    priceMax: parseInt(searchParams.get("priceMax") || "0"),
    per_page: parseInt(searchParams.get("perPage") || "4")
  })

  useEffect(() => {
    // Update filter when searchParams change and reset pagination
    const newFilter = {
      location: searchParams.get("location"),
      travellers: parseInt(searchParams.get("travellers") || "0"),
      startDate: searchParams.get("startDate"),
      endDate: searchParams.get("endDate"),
      lat: searchParams.get("lat"),
      lon: searchParams.get("lon"),
      space: searchParams.get("space") || "",
      description: searchParams.get("description") || "",
      activities: searchParams.get("activities") || "",
      type: searchParams.get("type") || "",
      services: searchParams.get("services") || "",
      bedrooms: parseInt(searchParams.get("bedrooms") || "0"),
      beds: parseInt(searchParams.get("beds") || "0"),
      bathrooms: parseInt(searchParams.get("bathrooms") || "0"),
      priceMin: parseInt(searchParams.get("priceMin") || "0"),
      priceMax: parseInt(searchParams.get("priceMax") || "0"),
      per_page: 30 // Reset pagination when filters change
    };

    setFilter(newFilter);
    setIsLoadingMore(false); // Reset loading state
  }, [searchParams])


  useEffect(() => {
    if (!filter.lat && !filter.lon && !filter.location) {

      if (typeof window === "undefined" || !navigator.geolocation) {
        console.error("Geolocalización no soportada o ejecutándose en servidor");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => setFilter({
          ...filter,
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        }),
        (error) => {
          console.error("Error obteniendo la ubicación:", error)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, [filter])

  
  const {
    isPending: isPendingFavoritos,
    error: errorFavoritos,
    data: favoritos,
  } = useQuery({
    queryKey: ["favoritos"],
    queryFn: () => {
      const requestOptions = {
        headers: {
          Authorization: myHeaders,
        },
      };
      return fetch(`${URL_API}/favorites`, requestOptions).then((res) =>
        res.json()
      );
    },
  });

  const { isPending, error, data } = useQuery({
    queryKey: ["alojamientos", filter],
    queryFn: () => {
      const cleanedFilter: any = Object.fromEntries(
        Object.entries(filter).filter(([_, value]) => {
          if (value === null || value === undefined) return false;
          if (typeof value === 'string' && value.trim() === '') return false;
          if (typeof value === 'number' && value === 0) return false;
          return true;
        })
      );

      const params = new URLSearchParams(cleanedFilter);

      return fetch(`${URL_API}/properties?${params}`).then((res) => res.json());
    },
  });


  // Scroll handler for infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

    if (isNearBottom && data?.data?.length < data?.total) {
      setIsLoadingMore(true);
      setFilter((prev: any) => ({
        ...prev,
        per_page: prev.per_page + 30
      }));
    }
  }, [isLoadingMore, data?.data?.length]);


  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);


  // Handle loading state reset when data arrives
  useEffect(() => {
    if (data && isLoadingMore) {
      setIsLoadingMore(false);
    }
  }, [data, isLoadingMore]);

  if (isPending || isPendingFavoritos)
    return (
      <div className="min-h-screen bg-white">
        {/* Header Skeleton */}
        <div className="border-b border-gray-200 px-6 py-1">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48" />
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4 rounded-full" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="flex flex-col lg:flex-row h-[calc(100vh-80px)]">
          {/* Left Panel - Grid Skeleton */}
          <div className="w-full lg:w-3/5 overflow-y-auto p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((index) => (
                <div key={index} className="space-y-4">
                  {/* Image skeleton */}
                  <Skeleton className="h-48 w-full rounded-lg" />
                  {/* Title skeleton */}
                  <Skeleton className="h-4 w-3/4" />
                  {/* Details skeleton */}
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                  {/* Price skeleton */}
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="w-6 h-6 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Map Skeleton */}
          <div className="w-full lg:w-2/5 h-[400px] lg:h-auto p-6">
            <Skeleton className="w-full h-full rounded-lg" />
          </div>
        </div>
      </div>
    );

  if (error || errorFavoritos) return <Error />;


  if (isMobile) {
    const chunkSize = isMobile ? 4 : 6;
    const chunkedDestinations = chunkArray(data.data, chunkSize);

    return (
      <div className="min-h-screen bg-white">
        <div className="border-b border-gray-200 px-6 py-1">
          <div className="flex items-center justify-between">
            <h1 className="md:text-sm lg:text-2xl font-medium text-gray-900">{data.total} Alojamientos</h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Heart className="w-4 h-4 text-red-500" />
              <span>Los precios incluyen todas las tarifas</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row">
          {/* Left Panel - Accommodation Grid */}
          {chunkedDestinations.map((chunk, idx) => (
            <SliderChunk
              key={idx}
              items={chunk}
              renderCard={(destination) => (
                <AccommodationCard
                  destination={destination}
                  setFavorito={setFavorito}
                  favoritos={favoritos}
                  user={me}
                />
              )}
            />
          ))}

          {/* Right Panel - Map */}
          <div className={`w-full lg:w-2/5 h-[500px] lg:h-auto p-6 overflow-hidden`}>
            <AccommodationMap
              accommodations={data}
              selectedAccommodation={""}
              userLocation={[filter?.lat, filter?.lon]}
              favoritos={favoritos}
              user={me}
            />
          </div>
        </div>
      </div>
    )
  }


  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-200 px-6 py-1">
        <div className="flex items-center justify-between">
          <h1 className="md:text-sm lg:text-2xl font-medium text-gray-900">{data.total} Alojamientos</h1>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Heart className="w-4 h-4 text-red-500" />
            <span>Los precios incluyen todas las tarifas</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-80px)]">
        {/* Left Panel - Accommodation Grid */}
        <div ref={scrollContainerRef} className="w-full lg:w-3/5 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.data.map((accommodation: any) => (
              <AccommodationCard
                key={accommodation.id}
                destination={accommodation}
                setFavorito={setFavorito}
                favoritos={favoritos}
                user={me ?? null}
              />
            ))}
          </div>

          {/* Loading more indicator */}
          {isLoadingMore && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                <span className="text-gray-600">Cargando más alojamientos...</span>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Map */}
        <div className={`w-full lg:w-2/5 h-[400px] lg:h-auto p-6 overflow-hidden`}>
          <AccommodationMap
            accommodations={data}
            selectedAccommodation={""}
            userLocation={[filter?.lat, filter?.lon]}
            favoritos={favoritos}
            user={me}
          />
        </div>

      </div>
    </div>
  )
};

export default Alojamientos;
